import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { InlineError } from "@/components/InlineError";
import { ScanBarcode, Boxes, ShieldCheck, HandCoins, Loader2, ArrowLeft, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login"); // login | forgot
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const [forgotErr, setForgotErr] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      await login(form.email, form.password);
      toast.success("Connexion réussie");
      nav("/");
    } catch (er) {
      setErr(apiErr(er.response?.data?.detail, er) || er.message);
    } finally { setLoading(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true); setForgotErr("");
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setSent(true);
    } catch (er) {
      setForgotErr(apiErr(er.response?.data?.detail, er) || er.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-gradient-to-b from-[#16324F] via-[#153050] to-[#0E2135] text-white p-12 relative overflow-hidden">
        <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-[#2563EB]/25 blur-3xl" />
        <div className="absolute -left-16 bottom-0 w-80 h-80 rounded-full bg-[#0E7490]/20 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#2563EB] to-[#0E7490] flex items-center justify-center font-bold text-xl font-head shadow-[0_8px_20px_-6px_rgba(37,99,235,.7)]">B</div>
          <div>
            <div className="text-2xl font-bold font-head leading-none">Biashara</div>
            <div className="text-white/50 text-sm">ERP multi-activité · SaaS</div>
          </div>
        </div>
        <div className="relative space-y-6">
          <h1 className="text-4xl font-bold font-head leading-tight">Un point de vérité unique<br />pour toute votre exploitation.</h1>
          <p className="text-white/70 max-w-md">Ventes, crédits, stock traçable, caisse et rapports consolidés — chaque mouvement enregistré, horodaté et attribué.</p>
          <div className="grid grid-cols-2 gap-4 max-w-md pt-2">
            {[[ScanBarcode, "Ventes & scan code-barres"], [HandCoins, "Créances & encaissements"], [Boxes, "Stock traçable & inventaires"], [ShieldCheck, "Audit & multi-tenant"]].map(([Icon, t], i) => (
              <div key={i} className="flex items-center gap-2.5 text-sm text-white/80">
                <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center"><Icon className="w-4 h-4" /></div>
                {t}
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-white/40 text-xs">© 2026 Biashara. Tous droits réservés.</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#F4F6FB]">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-xl bg-[#16324F] text-white flex items-center justify-center font-bold text-xl font-head">B</div>
            <div className="text-2xl font-bold font-head text-[#16324F]">Biashara</div>
          </div>

          {mode === "login" && (
            <>
              <h2 className="text-2xl font-bold font-head text-[#16324F]">Connexion</h2>
              <p className="text-sm text-[#64748B] mt-1 mb-6">Accédez à votre espace de gestion.</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="lbl">E-mail ou matricule</label>
                  <input type="text" className="inp" data-testid="login-email-input" value={form.email} onChange={set("email")} required placeholder="vous@exemple.com ou HAM-ALI-001" autoCapitalize="none" autoCorrect="off" />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <label className="lbl !mb-0">Mot de passe</label>
                    <button type="button" onClick={() => { setMode("forgot"); setSent(false); }} className="text-xs font-semibold text-[#2563EB] hover:underline" data-testid="forgot-password-link">Mot de passe oublié ?</button>
                  </div>
                  <div className="relative mt-1.5">
                    <input type={showPw ? "text" : "password"} className="inp !pr-11" data-testid="login-password-input" value={form.password} onChange={set("password")} required placeholder="••••••••" />
                    <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569]" data-testid="login-password-toggle" aria-label={showPw ? "Masquer" : "Afficher"}>{showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                  </div>
                </div>
                <button type="submit" disabled={loading} data-testid="login-submit-btn" className="btn-primary w-full">
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />} Se connecter
                </button>
                <InlineError message={err} title="Échec de la connexion" />
              </form>
              <div className="mt-6 p-3 rounded-lg bg-[#EFF6FF] border border-[#bfdbfe] text-xs text-[#1D4ED8]">
                Les comptes sont créés par le propriétaire ou un administrateur. Contactez votre administrateur pour obtenir un accès.
              </div>
            </>
          )}

          {mode === "forgot" && (
            <>
              <button type="button" onClick={() => setMode("login")} className="flex items-center gap-1.5 text-sm text-[#64748B] hover:text-[#16324F] mb-4" data-testid="back-to-login"><ArrowLeft className="w-4 h-4" /> Retour à la connexion</button>
              <h2 className="text-2xl font-bold font-head text-[#16324F]">Mot de passe oublié</h2>
              <p className="text-sm text-[#64748B] mt-1 mb-6">Saisissez votre e-mail : nous vous enverrons un lien pour réinitialiser votre mot de passe.</p>
              {sent ? (
                <div className="p-4 rounded-xl bg-[#DCFCE7] border border-[#bbf7d0] text-sm text-[#15803D]" data-testid="forgot-sent-message">
                  Si un compte est associé à cet e-mail, un lien de réinitialisation vient d'être envoyé. Vérifiez votre boîte de réception (valable 1 heure).
                </div>
              ) : (
                <form onSubmit={submitForgot} className="space-y-4">
                  <div>
                    <label className="lbl">E-mail</label>
                    <input type="email" className="inp" data-testid="forgot-email-input" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required placeholder="vous@exemple.com" />
                  </div>
                  <button type="submit" disabled={loading} data-testid="forgot-submit-btn" className="btn-primary w-full">
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />} Envoyer le lien
                  </button>
                  <InlineError message={forgotErr} title="Envoi impossible" />
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
